import { BreadcrumbProps } from '../../stories/Breadcrumb';

export const UrlSectionToTranslationKeyMap = {
  activity: 'breadcrumbs:task_type_change',
  'activity-history': 'breadcrumbs:task:activity_history',
  account: 'global:header:nav:my_account',
  'admin-config': 'breadcrumbs:admin_config',
  assets: 'asset:header:asset_list_breadcrumb',
  assistant: 'breadcrumbs:assistant',
  'branding-settings': 'admin_config:navigation:branding_settings',
  catalogues: 'admin_config:navigation:catalogues',
  chat: 'breadcrumbs:chat',
  conversation: 'task_messages:filter:section:message_type:conversation',
  communication: 'task:nav:communication',
  'custom-data-groups': 'admin_config:navigation:custom_data_groups',
  'custom-fields': 'admin_config:custom_fields:page_title',
  details: 'breadcrumbs:details',
  'ext-metadata': 'breadcrumbs:extended_metadata',
  files: 'task:nav:files',
  global: 'breadcrumbs:global_notifications',
  'global-preferences': 'my_account:global_preferences',
  'linked-tasks': 'breadcrumbs:linked_tasks',
  login: 'my_account:login_security',
  members: 'people:orgs:members',
  'messaging-channels': 'admin_config:navigation:messaging_channels',
  new: 'breadcrumbs:new',
  'new-group': 'admin_config:custom_data_groups:new_group:page_title',
  notes: 'asset:nav:notes',
  notifications: 'breadcrumbs:notifications',
  orgs: 'common:people:organisations',
  overview: 'breadcrumbs:overview',
  people: 'asset:nav:people',
  personal: 'breadcrumbs:personal',
  photos: 'asset:nav:photos',
  products: 'admin_config:navigation:products',
  'public-holiday-calendars': 'admin_config:navigation:public_holiday_calendars',
  'quick-links': 'breadcrumbs:quick_links',
  'registration-token': 'breadcrumbs:registration_token',
  security: 'breadcrumbs:security',
  'service-levels': 'admin_config:navigation:service_levels',
  'task-type-change': 'breadcrumbs:task_type_change',
  'performance-stats': 'performance_stats:page_title',
  'task-types': 'asset:nav:task_types',
  'task-escalation-rules': 'breadcrumbs:task_escalation_rules',
  tasks: 'breadcrumbs:tasks',
  teams: 'common:people:teams',
  users: 'people:people_tabs:users',
  whatsapp: 'admin_config:navigation:whatsapp',
};

interface InjectedBreadcrumb {
  id: string;
  index: number;
  breadcrumbs: BreadcrumbProps[];
}

interface GenerateBreadcrumbItemsParams {
  displayRoute: string;
  navigationRoute: string;
  injectedBreadcrumbs: InjectedBreadcrumb[];
  loadingUids: { [uid: string]: boolean };
  concealedSections: string[];
  t: (key: string) => string;
  copyableRefs?: Set<string>;
}

/**
 * Generates breadcrumb items from route segments and injected breadcrumbs
 * @param params - Object containing all parameters needed for breadcrumb generation
 * @returns Array of breadcrumb items
 */
export const generateBreadcrumbItems = (params: GenerateBreadcrumbItemsParams): BreadcrumbProps[] => {
  const {
    displayRoute,
    navigationRoute,
    injectedBreadcrumbs,
    loadingUids,
    concealedSections,
    t,
    copyableRefs = new Set(),
  } = params;
  const splitDisplayRoute = displayRoute.split('/').filter(Boolean);
  const splitNavigationRoute = navigationRoute.split('/').filter(Boolean);

  // Generate base breadcrumb items from the route, filtering out concealed sections
  const routeItems = splitDisplayRoute.map((displayItem, index) => ({
    displayItem,
    originalIndex: index,
    href: `/${splitNavigationRoute.slice(0, index + 1).join('/')}`,
    translationKey: UrlSectionToTranslationKeyMap[displayItem as keyof typeof UrlSectionToTranslationKeyMap],
    isLoading: loadingUids[displayItem] || false,
  }));

  // Filter out concealed sections
  const filteredItems = routeItems.filter(({ displayItem }) => !concealedSections.includes(displayItem));

  // Generate breadcrumb items with correct hrefs and isLast properties
  const baseBreadcrumbItems: BreadcrumbProps[] = filteredItems.map(
    ({ displayItem, href, translationKey, isLoading }, filteredIndex) => {
      const label = translationKey ? t(translationKey) : displayItem.charAt(0).toUpperCase() + displayItem.slice(1);

      return {
        label,
        href: href,
        isLast: filteredIndex === filteredItems.length - 1,
        loading: isLoading,
        copyRef: !isLoading && copyableRefs.has(displayItem) ? displayItem : undefined,
      };
    },
  );

  // Remove the 'app' section as we never want to see this
  const routeBreadcrumbs = baseBreadcrumbItems.slice(1);

  // If no injected breadcrumbs, return the route breadcrumbs
  if (injectedBreadcrumbs.length === 0) {
    return routeBreadcrumbs;
  }

  // Sort injected breadcrumbs by index to ensure proper insertion order
  const sortedInjectedBreadcrumbs = [...injectedBreadcrumbs].sort((a, b) => a.index - b.index);

  // Build the final breadcrumb array with injected breadcrumbs
  const finalBreadcrumbs: BreadcrumbProps[] = [];
  let routeIndex = 0;

  for (const injected of sortedInjectedBreadcrumbs) {
    // Add route breadcrumbs up to the injection point
    while (routeIndex < injected.index && routeIndex < routeBreadcrumbs.length) {
      finalBreadcrumbs.push(routeBreadcrumbs[routeIndex]);
      routeIndex++;
    }

    // Add the injected breadcrumbs
    finalBreadcrumbs.push(...injected.breadcrumbs);
  }

  // Add any remaining route breadcrumbs
  while (routeIndex < routeBreadcrumbs.length) {
    finalBreadcrumbs.push(routeBreadcrumbs[routeIndex]);
    routeIndex++;
  }

  // Update isLast property for the final breadcrumb
  if (finalBreadcrumbs.length > 0) {
    finalBreadcrumbs.forEach((breadcrumb, index) => {
      breadcrumb.isLast = index === finalBreadcrumbs.length - 1;
    });
  }
  return finalBreadcrumbs;
};
